import { ChangeEvent, useEffect, useState } from 'react';
import { Badge, Button, Card, Form, Stack } from 'react-bootstrap';
import Table from 'react-bootstrap/Table';
import { FaArrowLeft } from 'react-icons/fa6';
import { Link, To, useLocation, useNavigate, useParams } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { LinkCell } from '../../../../../components/tables/cellComponents/LinkCell';
import { useDataContext } from '../../../../../context';
import { useToast } from '../../../../../context/ToastContext';
import {
  useAddClientInvoicerMutation,
  useAddParentMutation,
  useAddRemitInvoceMutation,
  useAppendRemitInvoceMutation,
  useGetCourseInvoicerQuery
} from '../../../../../services/endpoints/finance/invoicer';
import { Scopes } from '../../../../../services/endpoints/people/user';
import { getCurrentUser } from '../../../../../services/helper';
import { GetQueryParams } from '../../../../../types/api.type';
import { InvoiceClient } from '../../../../../types/content.type';
import { Course } from '../../../../../types/course.type';
import { Invoice } from '../../../../../types/finance.types';
import { Organization } from '../../../../../types/venue.type';
import { userCanAdmin_Invoices } from '../Invoices/InvoicesView';
import { canSeeFinanceSummary } from '../../../../../utils/rbac';
import { getErrorMessage } from '../../../../../utils/utils';

const defaultScope = 'open';
const currentUser = getCurrentUser();

export const InvoicerView = () => {
  const params = useParams();

  const { addToast } = useToast();
  const navigate = useNavigate();

  const { locationOptions } = useDataContext();
  const [filters, setFilters] = useState<GetQueryParams>({
    scope: defaultScope
  });
  const [scopes, setScopes] = useState<Scopes>({});
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const isGlobalUser = currentUser?.isGlobal;
  const isFinanceAdmin = currentUser?.canSeeFinanceMenu;
  const location = useLocation();

  const { data, refetch } = useGetCourseInvoicerQuery(location?.state?.course?.id || +params?.id!);
  const [course, setCourse] = useState<Course>(location?.state?.course || {});
  const [createInvoicerClient] = useAddClientInvoicerMutation();
  const [addParentInvoicer] = useAddParentMutation();
  const [addRemit] = useAddRemitInvoceMutation();
  const [appendRemit] = useAppendRemitInvoceMutation();
  const [selectedOption, setSelectedOption] = useState<number>();
  const [invoiceClients, setInvoiceClients] = useState<InvoiceClient[]>();
  const [organizations, setOrganizations] = useState<Organization[]>();
  const [sendInvoicer, setSendInvoicer] = useState<boolean>(false);

  // Handle the radio button change event
  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(Number(event.target.value));
  };

  // Handle form submission to retrieve the selected option
  const handleSubmit = () => {
    alert(`Selected option: ${selectedOption}`);
  };

  useEffect(() => {
    if (data && data?.course?.venue?.invoice_clients) {
      setInvoiceClients(data?.course?.venue?.invoice_clients);
      setOrganizations(data?.course?.venue?.organizations);
      setCourse(data?.course);
    }
  }, [data]);

  // const setFilterBy = (filterBy: { operator: string, field: string, value: string }[]) => {
  //   setFilters((prev) => ({
  //     ...prev,
  //     data: {
  //       ...prev!.data,
  //       filterBy,
  //     }
  //   }))
  // };

  useEffect(() => {
    if (course && course.id) {
      setCourse(course);
    }
  }, [course]);

  const addClient = async () => {
    try {
      const res = await createInvoicerClient({
        course_id: course?.id,
        invoiceable_id: course?.venue_id,
        invoiceable_type: 'Venue'
      }).unwrap();
      // console.log('res', res);
      addToast('The client has been generated.', 'success');
      refetch();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const addOrganization = async (org_id: any) => {
    try {
      const res = await createInvoicerClient({
        course_id: course?.id,
        invoiceable_id: org_id,
        invoiceable_type: 'Organization'
      }).unwrap();
      addToast('The client has been generated.', 'success');
      refetch();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const addParent = async (courseId: any) => {
    try {
      const res = await addParentInvoicer({
        course_id: courseId
      }).unwrap();
      if (!res?.invoiceClients?.length) {
        addToast(
          'No new parents to add. They may already be listed above, or this session has no parent emails / registrations to build from.',
          'warning'
        );
        return;
      }
      addToast('The parent has been added.', 'success');
      refetch();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };

  const addRemitInvocer = async () => {
    setSendInvoicer(true);
    // console.log('selectedOption', selectedOption);
    if (!selectedOption) {
      addToast('Select a client', 'error');
      setSendInvoicer(false);
      return;
    }

    try {
      const res = await addRemit({
        course_id: course?.id,
        invoice_client_id: Number(selectedOption)
      }).unwrap();
      // console.log('add Remit ', res);
      if (res) {
        addToast('Invoice added correctly', 'success');
        refetch();
        setSendInvoicer(false);
      }
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
      setSendInvoicer(false);
    }
  };

  const appendInvoice = async (id: number) => {
    try {
      await appendRemit({
        course_id: course?.id,
        invoice_id: Number(id)
      }).unwrap();
      addToast(`An invoice has been generated`, 'success');
      refetch();
    } catch (e) {
      addToast(getErrorMessage(e), 'error');
    }
  };
  const countPaidInvoices = () =>
    Number(data?.course?.paid_invoices_count ?? course?.paid_invoices_count ?? 0);
  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center px-lg-5 border-bottom px-4 py-3"
        style={{ background: '#EFEFEF' }}
      >
        <Stack
          direction="horizontal"
          gap={2}
          className="ps-lg-3 ps-0"
        >
          <button
            className="btn btn-link text-info p-0"
            onClick={() => navigate(-1 as To)}
          >
            <FaArrowLeft
              size={20}
              className="d-lg-none"
            />
            <FaArrowLeft
              size={24}
              className="d-none d-lg-block"
            />
          </button>
          <h2
            className="d-lg-none text-secondary fw-semibold text-uppercase mb-0 pt-1"
            style={{ color: '#888888', fontSize: '1rem' }}
          >
            Sessions List
          </h2>
          <h2 className="d-none d-lg-block text-info fw-bold mb-0">Invoicer</h2>
        </Stack>
        <Stack
          direction="horizontal"
          className="d-none d-lg-flex"
        >
          {/* <SimpleButton variant="primary" height={36}>
            Generate invoice
          </SimpleButton> */}
        </Stack>
      </div>

      <div className="py-2">
        <Card>
          <Card.Body>
            <p>
              <a
                href="#"
                onClick={() => {
                  const queryParams = new URLSearchParams({
                    status: 'paid',
                    course_invoices_course_id_eq: `${course.id}`
                  });
                  navigate(`/admin/finance/invoices/?${queryParams.toString()}`);
                }}
                style={{ textDecoration: 'underline', color: 'blue', cursor: 'pointer' }}
              >
                {countPaidInvoices()} Invoices
              </a>
              {'  have been paid for this session.'}
            </p>

            {organizations &&
              organizations?.length > 0 &&
              organizations.map((org) => (
                <Card.Title>
                  Organization: {org.name}{' '}
                  <Button
                    variant="primary"
                    onClick={() => addOrganization(org.id ?? 0)}
                  >
                    Add Client
                  </Button>
                </Card.Title>
              ))}

            {organizations && organizations?.length > 0 && (
              <Form>
                {organizations &&
                  organizations?.map((organization) => {
                    const organizationsInvoiceClients = organization?.invoice_clients;
                    return (
                      <>
                        {organizationsInvoiceClients &&
                          organizationsInvoiceClients?.map((client) => {
                            return (
                              <>
                                <Form.Check
                                  type="radio"
                                  label={
                                    <Link
                                      style={{ textDecoration: 'underline' }}
                                      to={`/admin/invoice-clients/${client?.id}`}
                                    >
                                      Client # {client?.id}: {client?.name}
                                    </Link>
                                  }
                                  value={`${client?.id}`}
                                  name="radioGroup"
                                  checked={selectedOption === client?.id}
                                  onChange={handleRadioChange}
                                  style={{ color: 'black' }}
                                />
                                {client?.invoices?.length > 0 && (
                                  <Table
                                    striped
                                    bordered
                                    hover
                                  >
                                    <thead>
                                      <tr>
                                        <th>Invoice</th>
                                        <th>Sessions</th>
                                        <th>Status</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {client?.invoices?.length > 0 &&
                                        client?.invoices?.map((invoice) => {
                                          return (
                                            <tr>
                                              <td>
                                                <LinkCell
                                                  content={`Invoice: # ${invoice?.id}: ${invoice?.name}`}
                                                  url={
                                                    invoice?.id
                                                      ? `/admin/finance/invoices/${invoice?.id}`
                                                      : ''
                                                  }
                                                  underline
                                                />
                                              </td>
                                              <td>
                                                {invoice?.courses?.length
                                                  ? invoice.courses.map((course, index) => (
                                                      <div key={index}>{course.title}</div>
                                                    ))
                                                  : invoice?.name}
                                              </td>
                                              <td>
                                                <Stack
                                                  direction="horizontal"
                                                  gap={3}
                                                >
                                                  {invoice?.status === 'late' && (
                                                    <Badge bg="danger">
                                                      <span
                                                        style={{ color: 'white', fontSize: '1rem' }}
                                                      >
                                                        Late
                                                      </span>
                                                    </Badge>
                                                  )}
                                                  {invoice?.status === 'draft' && (
                                                    <Badge bg="info">
                                                      <span
                                                        style={{ color: 'white', fontSize: '1rem' }}
                                                      >
                                                        Draft
                                                      </span>
                                                    </Badge>
                                                  )}
                                                  {invoice?.status === 'sent' && (
                                                    <Badge
                                                      bg="#953E6A"
                                                      style={{ backgroundColor: '#953E6A' }}
                                                    >
                                                      <span
                                                        style={{ color: 'white', fontSize: '1rem' }}
                                                      >
                                                        Sent
                                                      </span>
                                                    </Badge>
                                                  )}

                                                  <Button
                                                    onClick={() => appendInvoice(invoice?.id)}
                                                  >
                                                    <span className="d-flex align-items-center">
                                                      <span
                                                        style={{
                                                          fontWeight: 'bold',
                                                          fontSize: '15px'
                                                        }}
                                                      >
                                                        +
                                                      </span>
                                                      <span className="ms-1">Append</span>
                                                    </span>
                                                  </Button>
                                                </Stack>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </tbody>
                                  </Table>
                                )}
                              </>
                            );
                          })}
                      </>
                    );
                  })}
              </Form>
            )}
            <hr></hr>

            <Card.Title>
              Venue: {course?.venue?.title}{' '}
              <Button
                variant="primary"
                onClick={() => addClient()}
              >
                Add Client
              </Button>
            </Card.Title>

            <Form>
              {invoiceClients &&
                invoiceClients?.map((client) => {
                  return (
                    <>
                      <Form.Check
                        type="radio"
                        label={
                          <Link
                            style={{ textDecoration: 'underline' }}
                            to={`/admin/invoice-clients/${client?.id}`}
                          >
                            Client # {client?.id}: {client?.name}
                          </Link>
                        }
                        value={`${client?.id}`}
                        name="radioGroup"
                        checked={selectedOption === client?.id}
                        onChange={handleRadioChange}
                        style={{ color: 'black' }}
                      />
                      {(client?.invoices?.length ?? 0) > 0 && (
                        <Table
                          striped
                          bordered
                          hover
                        >
                          <thead>
                            <tr>
                              <th>Invoice</th>
                              <th>Sessions</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(client?.invoices?.length ?? 0) > 0 &&
                              client?.invoices?.map((invoice) => {
                                return (
                                  <tr>
                                    <td>
                                      <LinkCell
                                        content={`Invoice: # ${invoice?.id}: ${invoice?.name}`}
                                        url={
                                          invoice?.id
                                            ? `/admin/finance/invoices/${invoice?.id}`
                                            : ''
                                        }
                                        underline
                                      />
                                    </td>
                                    <td>
                                      {invoice?.courses?.length
                                        ? invoice.courses.map((course, index) => (
                                            <div key={index}>{course.title}</div>
                                          ))
                                        : invoice?.name}
                                    </td>
                                    <td>
                                      <Stack
                                        direction="horizontal"
                                        gap={3}
                                      >
                                        {invoice?.status === 'late' && (
                                          <Badge bg="danger">
                                            <span style={{ color: 'white', fontSize: '1rem' }}>
                                              Late
                                            </span>
                                          </Badge>
                                        )}
                                        {invoice?.status === 'draft' && (
                                          <Badge bg="info">
                                            <span style={{ color: 'white', fontSize: '1rem' }}>
                                              Draft
                                            </span>
                                          </Badge>
                                        )}
                                        {invoice?.status === 'sent' && (
                                          <Badge
                                            bg="#953E6A"
                                            style={{ backgroundColor: '#953E6A' }}
                                          >
                                            <span style={{ color: 'white', fontSize: '1rem' }}>
                                              Sent
                                            </span>
                                          </Badge>
                                        )}

                                        <Button onClick={() => appendInvoice(invoice?.id)}>
                                          <span className="d-flex align-items-center">
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                              +
                                            </span>
                                            <span className="ms-1">Append</span>
                                          </span>
                                        </Button>
                                      </Stack>
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </Table>
                      )}
                    </>
                  );
                })}
            </Form>
            <hr></hr>

            <Card.Text>
              Parents: {course?.title} Clients{' '}
              <Button
                variant="primary"
                onClick={() => {
                  addParent(course?.id);
                }}
              >
                Add Parents
              </Button>
            </Card.Text>

            <Form>
              {data?.course?.contacts
                ?.filter((client) => (client?.invoice_clients?.length ?? 0) > 0)
                ?.map((client) => {
                  return (
                    <>
                      <Form.Check
                        type="radio"
                        label={
                          <Link
                            style={{ textDecoration: 'underline' }}
                            to={`/admin/invoice-clients/${client?.invoice_clients?.[0]?.id}`}
                          >
                            Client # {client?.id}: {client?.email}
                          </Link>
                        }
                        value={client?.invoice_clients?.[0]?.id}
                        name="radioGroup"
                        checked={selectedOption === client?.invoice_clients?.[0]?.id}
                        onChange={handleRadioChange}
                        style={{ color: 'black' }}
                      />
                      {client?.invoice_clients?.some(
                        (invclient) => invclient.invoices?.length > 0
                      ) && (
                        <Table
                          striped
                          bordered
                          hover
                        >
                          <thead>
                            <tr>
                              <th>Invoice</th>
                              <th>Sessions</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(client?.invoice_clients?.length ?? 0) > 0 &&
                              client?.invoice_clients?.map((invclient) => {
                                return invclient.invoices.map((invoice: any) => (
                                  <tr>
                                    <td>
                                      <LinkCell
                                        content={`Invoice: # ${invclient?.id}: ${invoice?.name}`}
                                        url={
                                          invoice?.id
                                            ? `/admin/finance/invoices/${invoice?.id}`
                                            : ''
                                        }
                                        underline
                                      />
                                    </td>
                                    <td>
                                      {invoice?.courses[0]
                                        ? invoice?.courses[0]?.title
                                        : invoice?.name}
                                    </td>
                                    <td>
                                      <Stack
                                        direction="horizontal"
                                        gap={3}
                                      >
                                        {invoice?.status === 'late' && (
                                          <Badge bg="danger">
                                            <span style={{ color: 'white', fontSize: '1rem' }}>
                                              Late
                                            </span>
                                          </Badge>
                                        )}
                                        {invoice?.status === 'draft' && (
                                          <Badge bg="info">
                                            <span style={{ color: 'white', fontSize: '1rem' }}>
                                              Draft
                                            </span>
                                          </Badge>
                                        )}
                                        {invoice?.status === 'sent' && (
                                          <Badge
                                            bg="#953E6A"
                                            style={{ backgroundColor: '#953E6A' }}
                                          >
                                            <span style={{ color: 'white', fontSize: '1rem' }}>
                                              Sent
                                            </span>
                                          </Badge>
                                        )}

                                        {/* <Button>{invoice?.status}</Button> */}
                                        <Button onClick={() => appendInvoice(invoice?.id)}>
                                          <span className="d-flex align-items-center">
                                            <span style={{ fontWeight: 'bold', fontSize: '15px' }}>
                                              +
                                            </span>
                                            <span className="ms-1">Append</span>
                                          </span>
                                        </Button>
                                      </Stack>
                                    </td>
                                  </tr>
                                ));
                              })}
                          </tbody>
                        </Table>
                      )}
                    </>
                  );
                })}
            </Form>

            {userCanAdmin_Invoices(currentUser) && (
              <Button
                variant="primary"
                onClick={addRemitInvocer}
                disabled={sendInvoicer}
              >
                Generate invoice
              </Button>
            )}
          </Card.Body>
        </Card>
      </div>
    </>
  );
};
